import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { productGroupsAPI, categoriesAPI } from '../../services/api'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { usePermissions } from '../../hooks/usePermissions'

export default function ProductGroupForm() {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { productGroups: productGroupPerms } = usePermissions()
  const isEditing = Boolean(uuid)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category_uuid: '',
    subcategory_uuid: '',
    is_active: true
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch existing product group for editing
  const { data: productGroupData, isLoading: isLoadingProductGroup } = useQuery(
    ['product-group', uuid],
    () => productGroupsAPI.getById(uuid),
    {
      enabled: isEditing,
      onSuccess: (data) => {
        const productGroup = data.data || data
        setFormData({
          name: productGroup.name || '',
          category_uuid: productGroup.category_uuid || '',
          subcategory_uuid: productGroup.subcategory_uuid || '',
          is_active: productGroup.is_active !== undefined ? productGroup.is_active : true
        })
      },
      onError: (error) => {
        toast.error('Failed to load product group details')
        console.error('Product group fetch error:', error)
      }
    }
  )

  // Fetch categories
  const { data: categoriesData } = useQuery(
    'categories',
    () => categoriesAPI.getAll(),
    {
      onError: (error) => {
        console.error('Categories fetch error:', error)
      }
    }
  )

  // No longer need subcategories since we're linking directly to categories

  // Create/Update mutations
  const createMutation = useMutation(
    (data) => productGroupsAPI.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('product-groups')
        toast.success('Product group created successfully!')
        navigate('/product-groups')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create product group')
      }
    }
  )

  const updateMutation = useMutation(
    (data) => productGroupsAPI.update(uuid, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('product-groups')
        queryClient.invalidateQueries(['product-group', uuid])
        toast.success('Product group updated successfully!')
        navigate('/product-groups')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update product group')
      }
    }
  )

  // Helper arrays
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData.data.data : []

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Handle category change
  const handleCategoryChange = (e) => {
    const categoryUuid = e.target.value
    setFormData(prev => ({
      ...prev,
      category_uuid: categoryUuid
    }))
    
    // Clear errors
    if (errors.category_uuid) {
      setErrors(prev => ({ ...prev, category_uuid: '' }))
    }
  }



  // Form validation
  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product group name is required'
    }
    
    if (!formData.category_uuid) {
      newErrors.category_uuid = 'Category is required'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Prepare data (no need for FormData since we're not uploading files)
      const submitData = {
        name: formData.name.trim(),
        category_uuid: formData.category_uuid,
        is_active: formData.is_active
      }
      
      if (isEditing) {
        await updateMutation.mutateAsync(submitData)
      } else {
        await createMutation.mutateAsync(submitData)
      }
    } catch (error) {
      console.error('Submit error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Permission check
  if (!productGroupPerms.canCreate && !isEditing) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to create product groups.</p>
        </div>
      </div>
    )
  }

  if (!productGroupPerms.canUpdate && isEditing) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to edit product groups.</p>
        </div>
      </div>
    )
  }

  if (isEditing && isLoadingProductGroup) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading product group...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/product-groups')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold gradient-text">
                {isEditing ? 'Edit Product Group' : 'Create Product Group'}
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                {isEditing ? 'Update product group information' : 'Add a new product group to your catalog'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="card-modern animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category_uuid"
                value={formData.category_uuid}
                onChange={handleCategoryChange}
                className={`input-modern w-full ${errors.category_uuid ? 'border-red-500' : ''}`}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.uuid} value={category.uuid}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category_uuid && <p className="mt-1 text-sm text-red-500">{errors.category_uuid}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Group Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Group Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`input-modern w-full ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Enter product group name"
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
            </div>

            {/* Active Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div className="flex items-center pt-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="checkbox-modern"
                />
                <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Active (visible to customers)
                </label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/product-groups')}
              className="btn-secondary px-6 py-2 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary px-6 py-2 rounded-lg flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{isEditing ? 'Update Product Group' : 'Create Product Group'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}